import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel } from '@material-ui/core';

import { CycleCountApi } from 'api/cycleCount';
import { InventoryAdjustmentApi } from 'api/inventoryAdjustment';
import { ActionModal } from 'components/warehouse/components/locationInventoryV2Components/ActionModal';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { SourceSelector } from './SourceSelector';
import { getActionDefinitions } from './cycleCountIssues/cycleCountIssueActionDefinitions';
import { MissingLotDetailCell } from './cycleCountIssues/missingLotDetailCell';
import { MissingPalletDetailCell } from './cycleCountIssues/missingPalletDetailCell';
import { PalletMovedToLocationDetailCell } from './cycleCountIssues/palletMovedToLocationDetailCell';
import { ReplenishmentUnitDiscrepancyDetailCell } from './cycleCountIssues/replenishmentUnitDiscrepancyDetailCell';
import { UnitDiscrepancyDetailCell } from './cycleCountIssues/unitDiscrepancyDetailCell';

const cycleCountApi = new CycleCountApi();
const inventoryAdjustmentApi = new InventoryAdjustmentApi();

function LocationIssuesTab() {
    const [rows, setRows] = useState([]);
    const [currentIssue, setCurrentIssue] = useState(null);
    const [notification, setNotification] = useState({ text: '', level: '' });
    const user = useContext(UserContext);
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [missingLocationId, setMissingLocationId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showOnlyBulkSelectable, setShowOnlyBulkSelectable] = useState(false);
    const [selectedSource, setSelectedSource] = useState('ALL');
    const isFirstShowOnlyBulkSelectableRender = useRef(true);
    const isFirstSelectedSourceRender = useRef(true);
    const [allIssues, setAllIssues] = useState([]);

    useEffect(() => {
        getLocationIssues();
    }, []);

    useEffect(() => {
        if (isFirstShowOnlyBulkSelectableRender.current) {
            isFirstShowOnlyBulkSelectableRender.current = false;
            return;
        }
        if (showOnlyBulkSelectable) {
            setRows(rows.filter((issue) => isBulkAdjustable(issue)));
        } else {
            setRows(allIssues);
        }
    }, [showOnlyBulkSelectable]);

    useEffect(() => {
        if (isFirstSelectedSourceRender.current) {
            isFirstSelectedSourceRender.current = false;
            return;
        }
        getLocationIssues();
    }, [selectedSource]);

    const handleActionClick = (issue) => {
        setActionModalOpen(true);
        setCurrentIssue(issue);
    };

    const getLocationIssues = async () => {
        setIsLoading(true);
        const res = await cycleCountApi.getLocationIssues(user.getFacilityID(), selectedSource);
        if (!res.status) {
            setNotification({ text: res.msg, level: 'error' });
            return;
        }

        setMissingLocationId(res.data.missingLocationId);
        // Map eventID to id and restrict selection to Unit Discrepancy rows
        const resultsWithId = (res.data.results || []).map((r) => ({
            ...r,
            id: r.eventID,
            canUserSelectBulkAction: isBulkAdjustable(r),
        }));
        setRows(resultsWithId);
        setAllIssues(resultsWithId);
        setIsLoading(false);
    };

    const isBulkAdjustable = (issue) => {
        const expected = issue.expectedResults?.[0]?.expectedCaseCount ?? 0;
        const actual = issue.expectedResults?.[0]?.actualCaseCount ?? 0;
        const caseDiff = Math.abs(expected - actual);
        return issue.issueType === 'Unit Discrepancy' && !issue.isReplenishmentCount && caseDiff <= 5;
    };

    const handleCloseModal = () => {
        setCurrentIssue(null);
        setActionModalOpen(false);
    };

    const handleSuccess = () => {
        handleCloseModal();
        getLocationIssues();
        setNotification({ text: 'Issue resolved', level: 'success' });
    };

    const handleError = (message) => {
        setNotification({ text: message, level: 'error' });
    };

    const handleBulkAdjust = async (issues) => {
        setIsLoading(true);
        try {
            const selectedIssues = rows.filter((row) => issues.includes(row.id));

            const adjustmentPromises = selectedIssues.flatMap((issue) =>
                // TODO: REPLEN disabled for now
                // if (issue.isReplenishmentCount) {
                //     return (issue.expectedResults || [])
                //         .filter((item) => item?.expectedPalletID > 0 || item?.totalExpectedUnits > 0)
                //         .map((item) =>
                //             submitAdjustment(
                //                 item?.expectedLotID,
                //                 item?.actualCaseCount,
                //                 item?.actualLooseUnitCount,
                //                 issue?.locationID,
                //                 item?.expectedPalletID,
                //             )
                //                 .then((result) => ({
                //                     ok: Boolean(result?.status && result?.data?.adjustmentsMade),
                //                     summaries: result?.data?.adjustmentSummaries || [],
                //                     issue,
                //                 }))
                //                 .catch(() => ({ ok: false, summaries: [], issue })),
                //         );
                // }

                [
                    submitAdjustment(issue.eventID, issue.totalUnitDifference)
                        .then((result) => ({
                            ok: Boolean(result?.status && result?.data?.adjustmentsMade),
                            summaries: result?.data?.adjustmentSummaries || [],
                            issue,
                        }))
                        .catch(() => ({ ok: false, summaries: [], issue })),
                ],
            );

            if (adjustmentPromises.length === 0) {
                setNotification({ text: 'No eligible adjustments to process.', level: 'warning' });
                return;
            }

            const results = await Promise.all(adjustmentPromises);

            const successResults = [];
            const errorResults = [];

            results.forEach(({ issue, ok, summaries }) => {
                if (ok) {
                    successResults.push(issue);
                } else {
                    errorResults.push(issue);
                }
            });

            const attempted = results.length;
            const succeeded = successResults.length;
            const failed = errorResults.length;

            const message = `Bulk adjustment complete: ${succeeded}/${attempted} succeeded${failed ? `, ${failed} failed` : ''}.`;

            setNotification({ text: message, level: failed ? 'warning' : 'success' });

            getLocationIssues();
        } finally {
            setIsLoading(false);
        }
    };

    const submitAdjustment = async (cycleCountEventId, unitAdjustment) =>
        await inventoryAdjustmentApi.resolveUnitDiscrepancyWithMagnitudeAdjustment(
            cycleCountEventId,
            unitAdjustment,
            'Cycle Count Adjustment',
            null,
        );

    const columns = [
        { Header: 'Time', accessor: 'timestamp' },
        { Header: 'User', accessor: 'authUser' },
        { Header: 'Issue', accessor: 'issueType' },
        { Header: 'Location', accessor: 'locationName' },
        {
            Header: 'Details',
            filterType: 'fuzzy',
            Cell: ({ row }) => {
                if (row.original.issueType === 'Unit Discrepancy' && row.original.isReplenishmentCount) {
                    return <ReplenishmentUnitDiscrepancyDetailCell row={row.original} />;
                }
                if (
                    (row.original.issueType === 'Unit Discrepancy' || row.original.issueType === 'Additional Sku') &&
                    row.original.expectedResults?.length === 1
                ) {
                    return <UnitDiscrepancyDetailCell row={row.original} />;
                }
                if (row.original.issueType === 'Missing Pallet' || row.original.issueType === 'Pallet Empty') {
                    return <MissingPalletDetailCell row={row.original} />;
                }
                if (row.original.issueType === 'Missing Lot') {
                    return <MissingLotDetailCell row={row.original} />;
                }
                if (row.original.issueType === 'Moved Pallet To Location') {
                    return <PalletMovedToLocationDetailCell row={row.original} />;
                }
                return <div>{row.original.details}</div>;
            },
        },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <Button
                    style={{ margin: '5px', textTransform: 'none' }}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleActionClick(row.original)}
                >
                    Actions
                </Button>
            ),
        },
    ];

    const bulkActions = [
        {
            name: 'Adjust Selected Rows',
            action: (issues) => {
                handleBulkAdjust(issues);
            },
        },
    ];

    const handleShowOnlyBulkSelectableChange = (e) => {
        setShowOnlyBulkSelectable(e.target.checked);
    };

    const handleSourceChange = (event) => {
        setSelectedSource(event.target.value);
    };

    return (
        <>
            <Notifications options={notification} />
            <div style={{ position: 'relative' }}>
                <FormControlLabel
                    control={
                        <Checkbox checked={showOnlyBulkSelectable} onChange={handleShowOnlyBulkSelectableChange} />
                    }
                    label="Show Only Bulk Selectable"
                    labelPlacement="end"
                />
                <SourceSelector value={selectedSource} onChange={handleSourceChange} />
                <DataTable
                    csvExport
                    csvExportFileName="cycle_count_issues"
                    columns={columns}
                    data={rows}
                    editable={false}
                    selectMultiple={showOnlyBulkSelectable}
                    tableContainerMaxHeight={800}
                    bulkActions={bulkActions}
                />
                {isLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.6)',
                            zIndex: 2,
                        }}
                    >
                        <CircularProgress />
                    </div>
                )}
            </div>
            <ActionModal
                actions={getActionDefinitions(
                    currentIssue,
                    handleCloseModal,
                    handleSuccess,
                    missingLocationId,
                    handleError,
                )}
                closeModal={() => {
                    setActionModalOpen(false);
                }}
                title="Resolve Cycle Count Issue"
                open={actionModalOpen}
                row={currentIssue}
            />
        </>
    );
}

export { LocationIssuesTab };
